import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { UploadCloud, X, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useImportLeadsMutation } from '../../api/leadsApi';
import { apiErrorMessage } from '../../utils/apiError';

// Columns that carry real meaning → mapped to lead fields. Everything else in the sheet is
// either dropped (id-noise like ad_id / form_id / is_organic) or, if it's a custom form
// question, folded into the lead's "Concern" so nothing useful is lost.
const KNOWN = new Set([
  'id', 'created_time', 'ad_id', 'ad_name', 'adset_id', 'adset_name',
  'campaign_id', 'campaign_name', 'form_id', 'form_name', 'is_organic', 'platform',
  'full_name', 'first_name', 'last_name', 'phone_number', 'phone', 'email', 'city',
]);

// Structural columns we deliberately throw away (pure ids / bookkeeping from the ad export).
const DROP = new Set(['ad_id', 'adset_id', 'campaign_id', 'form_id', 'form_name', 'is_organic', 'created_time', 'id']);

const norm = (h) => String(h ?? '').trim().toLowerCase().replace(/^﻿/, '');

// Robustly turn any uploaded file into rows. Real spreadsheets (.xlsx/.xls) are binary and must be
// read as raw bytes; CSV/TSV are text and can be UTF-8 OR UTF-16 (Meta Lead Ads exports are UTF-16,
// tab-separated). We decode the text ourselves with the browser's TextDecoder — picking the
// encoding from the byte-order mark — because the browser build of SheetJS can't decode UTF-16 on
// its own (it lacks the codepage tables the Node build loads), which is what threw the
// "could not read this file" error on the sample export.
function readSheetRows(bytes, fileName) {
  const name = String(fileName || '').toLowerCase();
  const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b;               // xlsx (PK zip)
  const isOle = bytes[0] === 0xd0 && bytes[1] === 0xcf;               // legacy .xls (OLE2)
  const isExcel = isZip || isOle || name.endsWith('.xlsx') || name.endsWith('.xls');

  let wb;
  if (isExcel) {
    wb = XLSX.read(bytes, { type: 'array' });
  } else {
    let text;
    if (bytes[0] === 0xff && bytes[1] === 0xfe) text = new TextDecoder('utf-16le').decode(bytes);
    else if (bytes[0] === 0xfe && bytes[1] === 0xff) text = new TextDecoder('utf-16be').decode(bytes);
    else text = new TextDecoder('utf-8').decode(bytes);
    // SheetJS auto-detects the delimiter (tab vs comma vs semicolon) from the text.
    wb = XLSX.read(text, { type: 'string' });
  }
  const sheet = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
}
const clean = (v) => String(v ?? '').trim();
// Meta prefixes values: phone as "p:+9198…", lead id as "l:2237…". Strip the leading tag.
const stripTag = (v) => clean(v).replace(/^[a-z]:/i, '');
const prettyQ = (k) => k.replace(/_/g, ' ').replace(/\?+$/, '').trim();

function mapRow(raw) {
  // Build a normalized-key view of the row.
  const r = {};
  Object.entries(raw).forEach(([k, v]) => { r[norm(k)] = v; });

  const first = clean(r.first_name);
  const last = clean(r.last_name);
  const fullName = clean(r.full_name) || [first, last].filter(Boolean).join(' ');
  const phone = stripTag(r.phone_number ?? r.phone);
  const externalLeadId = stripTag(r.id);

  const platform = clean(r.platform).toLowerCase();
  const source = platform === 'ig' ? 'InstagramAds' : platform === 'fb' ? 'Facebook' : 'MetaAds';

  // Fold any unrecognised columns (the Instant-Form questions) into Concern as "Question: Answer".
  const extras = Object.keys(r)
    .filter((k) => k && !KNOWN.has(k))
    .map((k) => { const a = clean(r[k]).replace(/_/g, ' '); return a ? `${prettyQ(k)}: ${a}` : null; })
    .filter(Boolean);

  return {
    externalLeadId: externalLeadId || null,
    fullName,
    phoneNumber: phone,
    email: clean(r.email) || null,
    city: clean(r.city) || null,
    source,
    sourceDetail: clean(r.form_name) ? `Meta Lead Form ${clean(r.form_name)}` : 'Imported from file',
    utmSource: platform || null,
    utmCampaign: clean(r.campaign_name) || null,
    adSetName: clean(r.adset_name) || null,
    adName: clean(r.ad_name) || null,
    concerns: extras.length ? extras.join('\n') : null,
    // UI-only:
    _valid: Boolean(fullName && phone),
  };
}

export default function ImportLeadsModal({ onClose }) {
  const [rows, setRows] = useState(null);   // parsed + mapped
  const [fileName, setFileName] = useState('');
  const [droppedCols, setDroppedCols] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const [importLeads, { isLoading: importing }] = useImportLeadsMutation();

  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true); setResult(null);
    try {
      const buf = await file.arrayBuffer();
      const json = readSheetRows(new Uint8Array(buf), file.name);
      if (!json.length) { toast.error('The sheet has no data rows.'); setParsing(false); return; }

      const headers = Object.keys(json[0]).map(norm);
      setDroppedCols(headers.filter((h) => DROP.has(h)));
      setRows(json.map(mapRow));
      setFileName(file.name);
    } catch (e) {
      toast.error('Could not read that file. Use a .csv, .xlsx or .xls export.');
    } finally {
      setParsing(false);
    }
  };

  const validRows = (rows ?? []).filter((r) => r._valid);
  const invalidCount = (rows ?? []).length - validRows.length;

  const doImport = async () => {
    if (validRows.length === 0) return;
    try {
      const res = await importLeads({
        items: validRows.map(({ _valid, ...keep }) => keep),
      }).unwrap();
      setResult(res);
      toast.success(`Imported ${res.imported} lead(s)`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Import failed'));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><FileSpreadsheet size={18} style={{ verticalAlign: -3, marginRight: 6 }} />Import leads from CSV / Excel</h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          {/* ── Result screen ── */}
          {result ? (
            <div className="empty-state" style={{ padding: 28 }}>
              <CheckCircle2 className="es-icon" size={44} style={{ color: 'var(--green-600, #16a34a)' }} />
              <h3>Import complete</h3>
              <p style={{ marginBottom: 4 }}><strong>{result.imported}</strong> new lead(s) added.</p>
              <p className="td-muted" style={{ fontSize: '0.85rem' }}>
                {result.skippedDuplicates} skipped as duplicates · {result.skippedInvalid} skipped (missing name/phone) ·
                {' '}{result.totalRows} rows read
              </p>
              <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={onClose}>Done</button>
            </div>
          ) : (
            <>
              {/* ── File picker ── */}
              {!rows && (
                <div
                  onClick={() => fileRef.current?.click()}
                  style={{
                    border: '2px dashed var(--border)', borderRadius: 10, padding: '32px 20px',
                    textAlign: 'center', cursor: 'pointer', background: 'var(--bg-subtle, rgba(0,0,0,0.02))',
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                >
                  <UploadCloud size={40} style={{ color: 'var(--blue-600, #2563eb)' }} />
                  <div style={{ fontWeight: 700, marginTop: 8 }}>
                    {parsing ? 'Reading file…' : 'Click to choose, or drag a file here'}
                  </div>
                  <div className="td-muted" style={{ fontSize: '0.82rem', marginTop: 4 }}>
                    Accepts .csv, .xlsx and .xls (e.g. a Meta Lead Ads export). Columns must match the sample sheet.
                  </div>
                  <input
                    ref={fileRef} type="file" accept=".csv,.tsv,.xlsx,.xls" hidden
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                </div>
              )}

              {/* ── Preview ── */}
              {rows && (
                <>
                  <div className="toolbar" style={{ gap: 10, alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600 }}>{fileName}</span>
                    <span className="badge b-green">{validRows.length} ready to import</span>
                    {invalidCount > 0 && (
                      <span className="badge b-amber" title="Rows missing a name or phone are skipped">
                        <AlertTriangle size={11} style={{ verticalAlign: -1 }} /> {invalidCount} will be skipped
                      </span>
                    )}
                    <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                      onClick={() => { setRows(null); setFileName(''); if (fileRef.current) fileRef.current.value = ''; }}>
                      Choose a different file
                    </button>
                  </div>

                  {droppedCols.length > 0 && (
                    <div className="td-muted" style={{ fontSize: '0.78rem', marginBottom: 8 }}>
                      Ignored id/bookkeeping columns: {droppedCols.join(', ')}
                    </div>
                  )}

                  <div className="tbl-wrap" style={{ maxHeight: 340, overflow: 'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th><th>Phone</th><th>City</th><th>Goal / concern</th>
                          <th>Campaign</th><th>Ad</th><th>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i} style={{ opacity: r._valid ? 1 : 0.45 }}>
                            <td className="td-bold">
                              {r.fullName || <span className="td-muted">— missing —</span>}
                            </td>
                            <td className="td-mono">{r.phoneNumber || <span className="td-muted">— missing —</span>}</td>
                            <td className="td-muted">{r.city || '—'}</td>
                            <td className="td-muted" style={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              title={r.concerns || ''}>{r.concerns || '—'}</td>
                            <td className="td-muted">{r.utmCampaign || '—'}</td>
                            <td className="td-muted">{r.adName || '—'}</td>
                            <td>{r.source}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {!result && (
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={!rows || validRows.length === 0 || importing} onClick={doImport}>
              {importing ? 'Importing…' : `Import ${validRows.length || ''} lead(s)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
